import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Lock } from 'lucide-react';
import { useDataPeriode, periodeVan } from '../../store/dataPeriodeStore';
import type { Grendel } from '../../services/dataCockpitApi';

/** Jaren in de filter: 2024 t/m het actuele jaar. */
const EERSTE_JAAR = 2024;

const selectKlasse =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-[#0B1F3A] focus:outline-none focus:ring-1 focus:ring-[#0B1F3A]';

/**
 * Universele filterbalk bovenaan elke DATA-submap. De keuze staat in een
 * gedeelde store en voedt alle vijf de submappen realtime.
 */
export const DataFilterBalk: React.FC = () => {
  const { t } = useTranslation('employer');
  const s = useDataPeriode();
  const [bereikOpen, setBereikOpen] = useState(s.modus === 'bereik');
  const [van, setVan] = useState(s.van);
  const [tot, setTot] = useState(s.tot);
  const huidigJaar = new Date().getFullYear();
  const jaren = Array.from({ length: huidigJaar - EERSTE_JAAR + 1 }, (_, i) => EERSTE_JAAR + i);
  const maanden = t('dataCockpit.filter.maanden', { returnObjects: true }) as string[];
  const p = periodeVan(s);
  const bereikGeldig = van && tot && van <= tot;

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          {t('dataCockpit.filter.jaar')}
          <select className={selectKlasse} value={s.jaar} disabled={s.modus === 'bereik'} onChange={(e) => s.kiesJaar(Number(e.target.value))}>
            {jaren.map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          {t('dataCockpit.filter.kwartaal')}
          <select
            className={selectKlasse}
            value={s.modus === 'kwartaal' ? s.kwartaal : ''}
            disabled={s.modus === 'bereik'}
            onChange={(e) => s.kiesKwartaal(e.target.value ? (Number(e.target.value) as 1 | 2 | 3 | 4) : null)}
          >
            <option value="">{t('dataCockpit.filter.heelJaar')}</option>
            {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          {t('dataCockpit.filter.maand')}
          <select
            className={selectKlasse}
            value={s.modus === 'maand' ? s.maand : ''}
            disabled={s.modus === 'bereik'}
            onChange={(e) => s.kiesMaand(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t('dataCockpit.filter.alleMaanden')}</option>
            {maanden.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => {
            if (bereikOpen && s.modus === 'bereik') s.kiesJaar(s.jaar);
            setBereikOpen(!bereikOpen);
          }}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
            s.modus === 'bereik' ? 'border-[#0B1F3A] bg-blue-50 text-[#0B1F3A]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <CalendarDays className="h-4 w-4" />
          {t('dataCockpit.filter.aangepast')}
        </button>
        {bereikOpen && (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              {t('dataCockpit.filter.vanaf')}
              <input type="date" className={selectKlasse} value={van} max={tot || undefined} onChange={(e) => setVan(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              {t('dataCockpit.filter.tot')}
              <input type="date" className={selectKlasse} value={tot} min={van || undefined} onChange={(e) => setTot(e.target.value)} />
            </label>
            <button
              type="button"
              disabled={!bereikGeldig}
              onClick={() => s.kiesBereik(van, tot)}
              className="rounded-lg bg-[#0B1F3A] px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {t('dataCockpit.filter.toepassen')}
            </button>
          </div>
        )}
        <p className="ml-auto self-center text-xs text-gray-500">
          {t('dataCockpit.filter.actief', {
            van: new Date(`${p.van}T00:00:00`).toLocaleDateString('nl-NL'),
            tot: new Date(`${p.tot}T00:00:00`).toLocaleDateString('nl-NL'),
          })}
        </p>
      </div>
    </div>
  );
};

/** De 🔒-melding die de frontend toont zodra de server data onderdrukt. */
export const PrivacyOnderdrukt: React.FC<{ grendel: Grendel; compact?: boolean }> = ({ grendel, compact }) => {
  const { t } = useTranslation('employer');
  const tekst =
    grendel.reden === 'FTE'
      ? t('dataCockpit.privacy.fte', { drempel: grendel.drempel })
      : grendel.reden === 'RESPONSES'
        ? t('dataCockpit.privacy.responses', { drempel: grendel.drempel })
        : t('dataCockpit.privacy.meldingen');
  return (
    <div className={`flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 ${compact ? 'p-3 text-xs' : 'p-4 text-sm'}`}>
      <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
      <p>{tekst}</p>
    </div>
  );
};

export const DataKop: React.FC<{ titel: string; ondertitel: string; icoon: React.ReactNode }> = ({ titel, ondertitel, icoon }) => (
  <div className="mb-4 flex items-start gap-3">
    <div className="rounded-xl bg-blue-50 p-2.5 text-[#0B1F3A]">{icoon}</div>
    <div>
      <h1 className="text-2xl font-bold text-gray-900">{titel}</h1>
      <p className="mt-0.5 text-sm text-gray-600">{ondertitel}</p>
    </div>
  </div>
);

export const Kaart: React.FC<{ titel?: string; uitleg?: string; actie?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
  titel,
  uitleg,
  actie,
  children,
  className = '',
}) => (
  <section className={`rounded-xl border border-gray-200 bg-white p-5 shadow-sm ${className}`}>
    {(titel || actie) && (
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          {titel && <h2 className="text-base font-semibold text-gray-900">{titel}</h2>}
          {uitleg && <p className="mt-0.5 text-xs text-gray-500">{uitleg}</p>}
        </div>
        {actie}
      </div>
    )}
    {children}
  </section>
);

export const KpiTegel: React.FC<{ label: string; waarde: React.ReactNode; sub?: React.ReactNode; accent?: string }> = ({ label, waarde, sub, accent }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900" style={accent ? { color: accent } : undefined}>{waarde}</p>
    {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
  </div>
);

export const Laden: React.FC = () => (
  <div className="animate-pulse space-y-4">
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => <div key={i} className="h-24 rounded-xl bg-gray-200" />)}
    </div>
    <div className="h-64 rounded-xl bg-gray-200" />
  </div>
);

export const Fout: React.FC<{ fout: unknown }> = ({ fout }) => {
  const { t } = useTranslation('employer');
  const msg = (fout as any)?.response?.data?.message;
  return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{msg || t('dataCockpit.fout')}</div>;
};
